import { Link } from 'react-router-dom';
import { useT } from '@/shared/i18n';
import { ROUTES } from '@/app/router/route-paths';
import { cn } from '@/shared/lib/cn';
import { Icon } from '@/shared/ui/icon';
import type { ReactNode } from 'react';

/**
 * PricingSection — the landing pricing block (extraction §16). Three tiers;
 * the middle "Growth" tier is the featured Ink card
 * with a Lime border + "Most popular" badge. No invented prices (Q7): tiers
 * show Free / Coming soon / Custom, the Monthly/Yearly toggle is a disabled
 * preview, and a launch note is shown.
 */
interface Tier {
  name: string;
  price: string;
  features: string[];
  cta: string;
  to: string;
  featured?: boolean;
  badge?: string;
}

export function PricingSection(): ReactNode {
  const { t } = useT();
  const tiers: Tier[] = [
    {
      name: t('landing.pricing.tier1.name'),
      price: t('landing.pricing.tier1.price'),
      features: [t('landing.pricing.tier1.f1'), t('landing.pricing.tier1.f2'), t('landing.pricing.tier1.f3')],
      cta: t('landing.pricing.tier1.cta'),
      to: ROUTES.auth.register,
    },
    {
      name: t('landing.pricing.tier2.name'),
      price: t('landing.pricing.tier2.price'),
      features: [t('landing.pricing.tier2.f1'), t('landing.pricing.tier2.f2'), t('landing.pricing.tier2.f3'), t('landing.pricing.tier2.f4')],
      cta: t('landing.pricing.tier2.cta'),
      to: ROUTES.auth.register,
      featured: true,
      badge: t('landing.pricing.tier2.badge'),
    },
    {
      name: t('landing.pricing.tier3.name'),
      price: t('landing.pricing.tier3.price'),
      features: [t('landing.pricing.tier3.f1'), t('landing.pricing.tier3.f2'), t('landing.pricing.tier3.f3')],
      cta: t('landing.pricing.tier3.cta'),
      to: ROUTES.plans,
    },
  ];

  return (
    <section id="pricing" className="scroll-mt-24 bg-surface-base py-32">
      <div className="mx-auto max-w-container-max px-margin-mobile text-center md:px-margin-desktop">
        <h2 className="mb-6 text-4xl font-bold tracking-tight text-on-surface md:text-5xl">
          {t('landing.pricing.heading')}
        </h2>
        {/* Monthly/Yearly toggle — disabled preview (Q7: non-misleading). */}
        <div className="mb-6 inline-flex items-center rounded-full border border-outline/20 bg-surface-container p-1.5">
          <span className="rounded-full bg-surface-container-lowest px-6 py-2 text-xs font-semibold uppercase tracking-wider text-on-surface shadow-sm">
            {t('landing.pricing.toggle.monthly')}
          </span>
          <span className="inline-flex items-center gap-2 px-6 py-2 text-xs font-semibold uppercase tracking-wider text-on-surface-variant/60">
            {t('landing.pricing.toggle.yearly')}
            <span className="rounded-full bg-secondary-fixed/15 px-2 py-0.5 text-[10px] text-secondary-fixed-dim">
              {t('landing.pricing.toggle.preview')}
            </span>
          </span>
        </div>
        <p className="mb-16 text-sm text-on-surface-variant">{t('landing.pricing.note')}</p>

        <div className="grid items-stretch gap-8 text-start lg:grid-cols-3">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={cn(
                'relative flex flex-col rounded-[32px] p-10',
                tier.featured
                  ? 'z-10 border-2 border-secondary-fixed bg-[var(--ink-band)] text-[var(--on-ink-band)] shadow-2xl lg:scale-105'
                  : 'border border-outline-variant/30 bg-surface-container-lowest transition-colors hover:border-secondary-fixed/40',
              )}
            >
              {tier.badge ? (
                <span className="absolute -top-4 start-10 rounded-full bg-secondary-fixed px-4 py-1 text-xs font-bold uppercase tracking-widest text-on-secondary-fixed">
                  {tier.badge}
                </span>
              ) : null}
              <div
                className={cn(
                  'mb-6 text-xs font-bold uppercase tracking-widest',
                  tier.featured ? 'text-secondary-fixed' : 'text-on-surface-variant',
                )}
              >
                {tier.name}
              </div>
              <div className={cn('mb-8 text-4xl font-bold', tier.featured ? 'text-[var(--on-ink-band)]' : 'text-on-surface')}>
                {tier.price}
              </div>
              <ul className="mb-10 flex-grow space-y-4">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Icon
                      name="check"
                      size={18}
                      className="mt-0.5 shrink-0 text-secondary-fixed"
                    />
                    <span className={cn('text-sm', tier.featured ? 'text-[var(--on-ink-band)]' : 'text-on-surface')}>
                      {f}
                    </span>
                  </li>
                ))}
              </ul>
              <Link
                to={tier.to}
                className={cn(
                  'mt-auto w-full rounded-xl py-3.5 text-center text-xs font-semibold uppercase tracking-wider transition-all active:scale-[0.98]',
                  tier.featured
                    ? 'bg-secondary-fixed text-on-secondary-fixed hover:opacity-90'
                    : 'border border-outline/30 text-on-surface hover:bg-surface-container',
                )}
              >
                {tier.cta}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default PricingSection;
